import { useVersionVariables } from '@client/hooks/use-element-expression-variables.hooks';
import { useBuildingLocation } from '@features/design/hooks/design.hooks';

import { toEnergyActivity } from '@shared/helpers/energy/energy-activity-type.helpers';
import {
  AIR_LEAKAGE_Q50,
  AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA,
  SOLAR_HEAT_GAIN_COEFFICIENT,
  SOLAR_SCREEN_FACTOR,
  TAP_WATER_RECIRCULATION_LOSS,
  THERMAL_BRIDGES_LOSS_FACTOR,
  WINDOW_GLASS_AREA_PERCENTAGE,
} from '@shared/helpers/energy/energy.models';
import { getHeatingHours } from '@shared/helpers/energy/heating-energy.helpers';
import { getBuildingEnvelopeArea } from '@shared/helpers/energy/thermal-transmittance.helpers';
import { calculateAirflow } from '@shared/helpers/energy/ventilation-energy.helpers';
import { toPercent } from '@shared/helpers/string.helpers';
import { Coordinate } from '@shared/models/geography.model';
import { capitalize, size } from 'lodash';
import React, { useMemo } from 'react';

import { NodonListItemResult } from '@ui/ListItemResult';
import {
  useBuildingEnvelopeElements,
  useBuildingHeatingDemand,
  useHeatedArea,
} from '@features/energy/energy.hook';
import { EnvelopeTransmittanceGroup } from './EnvelopeTransmittance';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';

export const HeatingDemandGroup: React.FC = () => {
  const heatingDemandResult = useBuildingHeatingDemand();
  const {
    heatingDemand,
    transmissionLosses,
    ventilationLosses,
    airingHeatingLosses,
    airLeakageLosses,
    activityHeating,
    occupantHeating,
    solarInsolationHeating,
    limitTemp,
    tapWaterRecirculationGain,
  } = heatingDemandResult;
  const errors =
    'errors' in heatingDemandResult ? heatingDemandResult.errors : undefined;
  const heatedArea = useHeatedArea();

  const elements = useBuildingEnvelopeElements();
  const buildingEnvelopeArea = getBuildingEnvelopeArea(elements);

  const location = useBuildingLocation();

  const versionVariables = useVersionVariables();
  const activity = toEnergyActivity(versionVariables.activity);

  const transmissionLossesKwh =
    transmissionLosses * (1 - THERMAL_BRIDGES_LOSS_FACTOR);

  const thermalBridgesLossesKwh =
    transmissionLosses * THERMAL_BRIDGES_LOSS_FACTOR;

  const heatingDemandPerSqm = heatedArea > 0 ? heatingDemand / heatedArea : 0;

  return (
    <NodonListItemResult
      name="Space heating demand"
      tooltip={[
        `The heating demand of the building (not including tapwater heating).`,
        `Total: ${getDisplayValueData({ value: heatingDemand, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
      ].join(' ')}
      value={heatingDemandPerSqm}
      unit="kWh/m^2"
      error={
        size(errors)
          ? 'Could not calculate heating demand due to invalid input. Check that your climate shell dimensions seems reasonable.'
          : undefined
      }
    >
      {/* Transmission losses */}
      <NodonListItemResult
        indentation={1}
        name="Total transmission losses"
        tooltip={[
          `The losses from the transmission of the building envelope including thermal bridges (${toPercent(THERMAL_BRIDGES_LOSS_FACTOR)}).`,
          `Total: ${getDisplayValueData({ value: transmissionLosses, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? transmissionLosses / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.transmissionLosses}
      >
        {/* Building envelope area */}
        <NodonListItemResult
          indentation={2}
          name="Building envelope area"
          tooltip={`The area of the building envelope.`}
          value={buildingEnvelopeArea}
          unit="m^2"
        />

        <EnvelopeTransmittanceGroup indentation={2} />

        {/* Transmission losses (without thermal bridges) */}
        <NodonListItemResult
          indentation={2}
          name="Transmission losses"
          tooltip={[
            `The transmission losses per m² of the building envelope excluding thermal bridges.`,
            `Total: ${getDisplayValueData({ value: transmissionLossesKwh, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
          ].join(' ')}
          value={
            buildingEnvelopeArea > 0
              ? transmissionLossesKwh / buildingEnvelopeArea
              : 0
          }
          unit="kWh/m^2"
        />

        {/* Thermal bridges */}
        <NodonListItemResult
          indentation={2}
          name="Thermal bridges"
          tooltip={[
            `The losses from the thermal bridges in the building envelope.`,
            `This is calculated by a ${toPercent(THERMAL_BRIDGES_LOSS_FACTOR)} template from Boverket.`,
            `Total: ${getDisplayValueData({ value: thermalBridgesLossesKwh, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
          ].join(' ')}
          value={
            buildingEnvelopeArea > 0
              ? thermalBridgesLossesKwh / buildingEnvelopeArea
              : 0
          }
          unit="kWh/m^2"
        />
      </NodonListItemResult>

      {/* Ventilation losses */}
      <NodonListItemResult
        indentation={1}
        name="Ventilation losses"
        tooltip={[
          `The losses from the ventilation of the building.`,
          `Total: ${getDisplayValueData({ value: ventilationLosses, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? ventilationLosses / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.ventilationLosses}
      />

      {/* Airing heating losses */}
      <NodonListItemResult
        indentation={1}
        name="Airing heating losses"
        tooltip={[
          `The losses from the airing (open doors, windows, etc.) of the building.`,
          `Template from Boverket: ${getDisplayValueData({ value: AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA, unit: 'kWh/m^2' }).full}.`,
          `Total: ${getDisplayValueData({ value: airingHeatingLosses, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? airingHeatingLosses / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.airingHeatingLosses}
      />

      {/* Air leakage losses */}
      <NodonListItemResult
        indentation={1}
        name="Air leakage losses"
        tooltip={[
          `The losses from the air leakage through the building envelope.`,
          `Total: ${getDisplayValueData({ value: airLeakageLosses, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? airLeakageLosses / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.airLeakageLosses}
      >
        {/* Air leakage */}
        <NodonListItemResult
          indentation={2}
          name="Air leakage"
          tooltip={`Constant for air leakage through the building envelope. Currently not editable.`}
          value={AIR_LEAKAGE_Q50}
          unit="l/(s*m^2)"
        />

        {/* Leakage flow */}
        <NodonListItemResult
          indentation={2}
          name="Leakage airflow"
          tooltip={`Total airflow leaking from through the building envelope walls.`}
          value={calculateAirflow(0, AIR_LEAKAGE_Q50, heatedArea)}
          unit="m^3/s"
        />

        {/* Heating operation hours */}
        <HeatingHoursItem indentation={2} location={location} />
      </NodonListItemResult>

      {/* Activity heating */}
      <NodonListItemResult
        indentation={1}
        name="Activity heating"
        tooltip={[
          `The heating generated by the activity of the building. Like heat generated by computers, lights, etc.`,
          `Total: ${getDisplayValueData({ value: activityHeating, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? -activityHeating / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.activityHeating}
      >
        {/* Heating operation hours */}
        <HeatingHoursItem indentation={2} location={location} />
      </NodonListItemResult>

      {/* Occupant heating */}
      <NodonListItemResult
        indentation={1}
        name="Occupant heating"
        tooltip={[
          `Heat generated by the occupants (people) in the building.`,
          `Total: ${getDisplayValueData({ value: occupantHeating, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? -occupantHeating / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.occupantHeating}
      >
        {/* Occupant count */}
        <NodonListItemResult
          indentation={2}
          name="Occupant count"
          tooltip={`The number of occupants in the building.`}
          value={versionVariables.occupant_count}
          unit="people"
        />

        {/* Activity */}
        <NodonListItemResult
          indentation={2}
          name="Activity"
          tooltip={`The activity of the building.`}
          value={capitalize(activity)}
          unit="activity"
        />

        {/* Heating operation hours */}
        <HeatingHoursItem indentation={2} location={location} />
      </NodonListItemResult>

      {/* Solar insolation heating */}
      <NodonListItemResult
        indentation={1}
        name="Solar insolation heating"
        tooltip={[
          `Heat received from solar insolation (through windows & balcony doors).`,
          `Total: ${getDisplayValueData({ value: solarInsolationHeating, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`,
        ].join(' ')}
        value={heatedArea > 0 ? -solarInsolationHeating / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.solarInsolationHeating}
      >
        {/* Average solar insolation TODO FIX THIS LATER */}
        {/* <NodonListItemResult
          indentation={2}
          name="Average solar insolation"
          tooltip={`The average solar insolation at the building location. We currently assume that 25% of the windows is facing each direction.`}
          value={averageInsolation}
          unit="kWh/m^2"
        >
          
          <NodonListItemResult
            indentation={3}
            name="Location insolation"
            tooltip={`The solar insolation received by the geographical location. This is not considering factors like shading, etc.`}
            value={solarInsolation}
            unit="kWh/m^2"
          />

          
          {getEntries(solarInsolationByDirections)
            .sort(entrySortFn)
            .map(([direction, value]) => (
              <NodonListItemResult
                key={direction}
                indentation={3}
                tooltip={`The solar insolation received by the ${direction} side of the building. 
                We assume that ${toPercent(SOLAR_INSOLATION_DIRECTION_FACORS[direction])} of the location's solar insolation is received by windows on the ${direction} side of the building.`}
                name={`Side ${direction} (${formatValue(
                  windowArea / 4,
                  'm²',
                )})`}
                value={value}
                unit="kWh/m^2"
              />
            ))} 
        </NodonListItemResult> 
        */}

        {/* Window glass area percentage */}
        <NodonListItemResult
          indentation={2}
          name="Window glass area percentage"
          tooltip={`The percentage of the window area that is glass and not frame etc.`}
          value={WINDOW_GLASS_AREA_PERCENTAGE}
          unit="%"
        />

        {/* Solar heat gain coefficient */}
        <NodonListItemResult
          indentation={2}
          name="Solar heat gain coefficient"
          tooltip={`The g-value (sometimes also called a Solar Factor or Total Solar Energy Transmittance) is the coefficient commonly used in Europe to measure the solar energy transmittance of windows.
          Despite having minor differences in modeling standards compared to the SHGC, the two values are effectively the same. A g-value of 1.0 represents full transmittance of all solar radiation while 0.0 represents a window with no solar energy transmittance.
          In practice though, most g-values will range between 0.2 and 0.7, with solar control glazing having a g-value of less than 0.5`}
          value={SOLAR_HEAT_GAIN_COEFFICIENT}
          unit="%"
        />

        {/* Solar screen factor */}
        <NodonListItemResult
          indentation={2}
          name="Solar screen factor"
          tooltip={`Solar shading coefficient (screening). How solar energy is left after curtains etc have "screened" the sun.
          0 means that all sun light is blocked, 1 means that no sun light is blocked.`}
          value={SOLAR_SCREEN_FACTOR}
          unit="%"
        />

        {/* Heating operation hours */}
        <HeatingHoursItem indentation={2} location={location} />
      </NodonListItemResult>

      {/* Tap water recirculation gain */}
      <NodonListItemResult
        indentation={1}
        name="Tap water recirculation gain"
        tooltip={`The losses from the tap water recirculation. Total: ${getDisplayValueData({ value: -tapWaterRecirculationGain, unit: 'kWh' }, { divideBy: { unit: 'yr' } }).full}`}
        value={heatedArea > 0 ? -tapWaterRecirculationGain / heatedArea : 0}
        unit="kWh/m^2"
        error={errors?.tapWaterRecirculationGain}
      >
        {/* Tap water recirculation loss per meter */}
        <NodonListItemResult
          indentation={2}
          name="Loss per meter"
          tooltip={`The loss from the tap water recirculation per meter of pipe. Template from Boverket.`}
          value={TAP_WATER_RECIRCULATION_LOSS}
          unit="kW/m"
        />
        {/* Pipe length */}
        {/* <NodonListItemResult
          indentation={2}
          name="Pipe length"
          tooltip={`The estimated length of the tap water pipes in the building. Based on the size and activity of the building.`}
          value={getTapWaterPipeLength(heatedArea, activity)}
          unit="m"
        /> */}
      </NodonListItemResult>

      {/* Limit temperature */}
      <NodonListItemResult
        name="Limit temperature"
        tooltip="The limit temperature used to calculate the degree hours. Currently not editable."
        value={limitTemp}
        unit="°C"
        indentation={1}
        error={errors?.limitTemp}
      />
    </NodonListItemResult>
  );
};

const HeatingHoursItem: React.FC<{
  indentation: number;
  location: Coordinate;
}> = ({ indentation, location }) => {
  const heatingDemandHours = useMemo(() => {
    return getHeatingHours({
      location,
    });
  }, [location]);

  return (
    <NodonListItemResult
      indentation={indentation}
      name="Heating demand hours"
      tooltip={`The number of hours per year that the building needs to be heated. The other hours hours any additional heat production is redundant (or might require cooling) `}
      value={heatingDemandHours}
      unit="h/yr"
    />
  );
};
